import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios';
import { toast } from 'react-toastify';
const backendUrl = process.env.REACT_APP_BACKEND_URL;

const initialState = {
  all_product: [],
  totalProducts: 0,
  currentPage: 1,
  cartDetails: {},
  singleProduct: null,
  cartItems: [],
  loading: false,
  error: null,
}

// Fetch Products
export const fetchProducts = createAsyncThunk('cart/fetchProducts', async ({ category, sortBy, page, limit }) => {
  const res = await axios.get(`${backendUrl}/fetch-products/`, { params: { category, sortBy, page, limit } });

  if (res.data.success) {
    return {
      products: res.data.products,
      totalProducts: res.data.totalProducts,
      currentPage: res.data.currentPage,
      totalPages: res.data.totalPages,
      isLoadMore: page > 1,
    };
  } else {
    return {
      products: [],
      totalProducts: 0,
      currentPage: 1,
      totalPages: 1,
      isLoadMore: false,
    };
  }
})

// Fetch Cart Items
export const fetchCart = createAsyncThunk('cart/fetchCart', async ({ page, limit }, { dispatch, rejectWithValue }) => {
  try {
    const { data } = await axios.get(`${backendUrl}/api/cart/get-cart`, { params: { page, limit } }, {
      withCredentials: true
    })
    if (!data.success) {
      dispatch(resetCart());
      return rejectWithValue(data.msg);
    }

    return data
  } catch (error) {
    dispatch(resetCart());
    return rejectWithValue(error.message)
  }
})

// Add To Cart
export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async ({ productId, unitPrice, variations }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${backendUrl}/api/cart/add-to-cart`,
        { productId, unitPrice, variations },
        { withCredentials: true }
      );

      if (data.success) {
        toast.success(data.msg);
        return data;
      } else {
        toast.error(data.message);
        return data;
      }
    } catch (error) {
      console.error("AddToCart Error:", error);
      return rejectWithValue(error.msg);
    }
  }
);

// Remove From Cart
export const removeFromCart = createAsyncThunk(
  'cart/removeFromCart',
  async (_id, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${backendUrl}/api/cart/remove-from-cart`,
        { _id },
        { withCredentials: true }
      );

      if (!data.success) {
        return rejectWithValue(data.errors);
      }
      return _id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
)

// Fetch single product
export const fetchSingleProduct = createAsyncThunk(
  'cart/fetchSingleProduct',
  async (productId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${backendUrl}/single-product/${productId}`, { withCredentials: true });
      if (!data.success) return rejectWithValue(data.msg);
      return data.product;
    } catch (error) {
      console.error("Fetch Single Product Error:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Checkout Session
export const checkoutSession = createAsyncThunk(
  'cart/checkoutSession',
  async ({ paymentMethod }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${backendUrl}/api/cart/create-checkout-session`,
        { paymentMethod },
        { withCredentials: true }
      );
      if (!data.success) return rejectWithValue(data.msg);
      return { paymentMethod, res: data };
    } catch (error) {
      console.error("Checkout Session Error:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Update Cart Qty
export const updateCartQty = createAsyncThunk(
  'cart/updateCartQty',
  async ({ cartId, type }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${backendUrl}/api/cart/update-qty`,
        { cartId, type },
        { withCredentials: true }
      );

      if (!data.success) return rejectWithValue(data.msg);
      return { cartId, type, qty: data.qty || 0, removed: data.removed || false };
    } catch (error) {
      console.error("UpdateCartQty Error:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Function to update Cart Details
const syncProductQty = (state, cartId, type = false, qty = false, removed = false) => {
  const itemIndex = state.cartItems.findIndex(i => i._id === cartId);
  const currentItem = state.cartItems[itemIndex];
  const totalRows = state.cartDetails.cartRows;
  const totalCount = state.cartDetails.totalCartCount;
  const totalAmount = state.cartDetails.totalCartAmount;
  const currentItemPrice = currentItem?.unitPrice || 0;
  const currentItemQty = currentItem?.qty || 0;

  // Update or remove item from current visible cartItems
  if (removed) {
    if (itemIndex !== -1) {
      state.cartItems.splice(itemIndex, 1);
      state.cartDetails.cartRows = Math.max(0, totalRows - 1);
    }
  } else if (itemIndex !== -1) {
    currentItem.qty = qty;
  }

  // Update total count & amount
  if (!removed && itemIndex !== -1) {

    if (type === "increase") {
      state.cartDetails.totalCartCount += 1;
      state.cartDetails.totalCartAmount += currentItemPrice;
    } else if (type === "decrease") {
      state.cartDetails.totalCartCount = Math.max(0, totalCount - 1);
      state.cartDetails.totalCartAmount = Math.max(0, totalAmount - currentItemPrice);
    }
  } else if (removed) {
    const removedTotal = currentItem ? currentItemPrice * currentItemQty : 0;
    state.cartDetails.totalCartAmount = Math.max(0, totalAmount - removedTotal);
    state.cartDetails.totalCartCount = Math.max(0, totalCount - currentItemQty);
  }
};

export const shopSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setProducts: (state, action) => {
      state.all_product = action.payload
    },
    totalProducts: (state, action) => {
      state.totalProducts = action.payload
    },
    clearProducts: (state) => {
      state.all_product = [];
      state.totalProducts = 0;
      state.currentPage = 1;
      state.totalPages = 1;
    },
    resetCart: (state) => {
      state.cartDetails = { ...initialState.cartDetails };
      state.cartItems = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // === FETCH PRODUCTS ===
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        const { products, totalProducts, currentPage, totalPages, isLoadMore } = action.payload;

        // Handle load more
        if (isLoadMore && currentPage > 1) {
          // Append new products for load more
          const existingIds = new Set(state.all_product.map(p => p._id));
          const uniqueNewProducts = products.filter(p => !existingIds.has(p._id));
          state.all_product = [...state.all_product, ...uniqueNewProducts];
        } else {
          // Replace products for initial load or sort/category change
          state.all_product = products;
        }

        state.totalProducts = totalProducts;
        state.currentPage = currentPage;
        state.totalPages = totalPages;
        state.loading = false;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // === FETCH CART ===
      .addCase(fetchCart.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.cartItems = action.payload.cartItems || [];
        state.cartDetails = {
          totalCartCount: action.payload.totalCartItems || 0,
          totalCartAmount: action.payload.totalCartAmount || 0,
          cartRows: action.payload.cartRows || 0
        }
        state.loading = false;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // === ADD TO CART ===
      .addCase(addToCart.fulfilled, (state, action) => {
        if (action.payload.success) state.cartDetails.totalCartCount++;
      })

      // === UPDATE CART QTY ===
      .addCase(updateCartQty.fulfilled, (state, action) => {
        const { cartId, type, qty, removed } = action.payload;
        syncProductQty(state, cartId, type, qty, removed);
      })

      // === REMOVE FROM CART ===
      .addCase(removeFromCart.fulfilled, (state, action) => {
        const cartId = action.payload;
        syncProductQty(state, cartId, false, false, true);
      })

      // Checkout Session
      .addCase(checkoutSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkoutSession.fulfilled, (state, action) => {
        state.loading = false;
        state.cartItems = [];
        state.cartDetails = {
          totalCartCount: 0,
          totalCartAmount: 0,
          cartRows: 0
        }

      })
      .addCase(checkoutSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // === FETCH SINGLE PRODUCT ===
      .addCase(fetchSingleProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSingleProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.singleProduct = action.payload;
      })
      .addCase(fetchSingleProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
  }
})

export const { setProducts, clearProducts, resetCart } = shopSlice.actions

// Calculate Total Cart Amount
export const getTotalCartAmount = (state) => {
  return state.cart.cartDetails.totalCartAmount;
};

// Calculate Total Cart Items
export const getTotalCartItems = (state) => {
  return state.cart.cartDetails.totalCartCount;
};

// Get Total Cart Rows
export const getTotalCartRows = (state) => {
  return state.cart.cartDetails.cartRows;
};

export default shopSlice.reducer